import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Detects a new server deploy and surfaces a reload prompt.
//
// One mount-time effect runs `check()` immediately, then on a 30-second
// interval plus on `focus`, `online`, `visibilitychange`. Each check fetches
// /api/health (cache-busted) and reads buildVersion:
//   - First success → capture bootedVersion (this tab's version).
//   - Later response with a different buildVersion → surface the banner +
//     a 30-second countdown auto-reload.
// Failed fetches (server bouncing during deploy, brief offline) swallowed.
//
// A small status pill is always pinned bottom-right of the viewport so the
// watcher state is inspectable without DevTools: green dot = up to date,
// orange = update detected (banner is visible), red = recent fetch error.
// Hover it for booted/server versions + last poll time.
//
// All steps still log under [deploy-watch] for DevTools.

const POLL_INTERVAL_MS = 30 * 1000;
const COUNTDOWN_SECONDS = 30;
const LOG_PREFIX = '[deploy-watch]';

function shortVersion(v) {
  if (!v) return '';
  return v.length > 8 ? v.slice(0, 7) : v;
}

function formatTimeAgo(ts) {
  if (!ts) return 'never';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function formatLocalDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function DeployUpdateBanner() {
  const [bootedVersion, setBootedVersion] = useState(null);
  const [bootedAt, setBootedAt] = useState(null); // server-reported boot time of the build this tab is on
  const [latestVersion, setLatestVersion] = useState(null);
  const [serverVersion, setServerVersion] = useState(null); // most recent successful poll
  const [lastPollAt, setLastPollAt] = useState(null);
  const [pollError, setPollError] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [later, setLater] = useState(false);
  const [pillOpen, setPillOpen] = useState(false);
  const [, setTick] = useState(0); // forces re-render so "last poll Ns ago" stays fresh

  const bootedRef = useRef(null);

  async function fetchVersion() {
    try {
      // Cache-bust defensively in case some middlebox ignores `no-store`.
      const res = await fetch(`/api/health?t=${Date.now()}`, { credentials: 'include', cache: 'no-store' });
      if (!res.ok) return { error: `HTTP ${res.status}` };
      const data = await res.json();
      const v = data?.buildVersion || null;
      return v ? { version: v, bootedAt: data?.buildBootedAt || null } : { error: 'no buildVersion in response' };
    } catch (err) {
      return { error: err.message || 'fetch failed' };
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function check(trigger) {
      const r = await fetchVersion();
      if (cancelled) return;
      setLastPollAt(Date.now());
      if (r.error) {
        setPollError(r.error);
        console.log(`${LOG_PREFIX} ${trigger || 'check'}: error (${r.error})`);
        return;
      }
      setPollError(null);
      setServerVersion(r.version);
      if (bootedRef.current == null) {
        bootedRef.current = r.version;
        setBootedVersion(r.version);
        setBootedAt(r.bootedAt);
        console.log(`${LOG_PREFIX} ${trigger || 'check'}: booted=${r.version} bootedAt=${r.bootedAt}`);
        return;
      }
      if (r.version !== bootedRef.current) {
        console.log(`${LOG_PREFIX} ${trigger || 'check'}: NEW! booted=${bootedRef.current} latest=${r.version}`);
        setLatestVersion(r.version);
      } else {
        console.log(`${LOG_PREFIX} ${trigger || 'check'}: same (${r.version})`);
      }
    }

    check('mount');

    const interval = setInterval(() => check('interval'), POLL_INTERVAL_MS);
    // 5-second re-render so the "Ns ago" label in the pill stays fresh
    const tickInterval = setInterval(() => setTick(t => t + 1), 5000);
    const onFocus = () => check('focus');
    const onOnline = () => check('online');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check('visibility');
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearInterval(tickInterval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!latestVersion || later) return;
    setCountdown(COUNTDOWN_SECONDS);
    const tick = setInterval(() => {
      setCountdown(prev => {
        if (prev == null) return null;
        if (prev <= 1) {
          clearInterval(tick);
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [latestVersion, later]);

  const pillDot = pollError
    ? '#dc2626'
    : latestVersion
      ? '#ea580c'
      : bootedVersion
        ? '#16a34a'
        : '#94a3b8';

  // The status pill lives at the foot of the sidebar rather than floating over
  // the page. It is portalled there so the polling below stays a single
  // instance shared with the update banner, which has to stay in the content
  // area. The host only exists after the shell has mounted.
  const [pillHost, setPillHost] = useState(null);
  useEffect(() => { setPillHost(document.getElementById('sidebar-status')); }, []);

  // Sitting at the foot of a long menu, the expanded detail panel opens below
  // the fold on a phone. Bring it into view — the sidebar is the scroller.
  const pillRef = useRef(null);
  useEffect(() => {
    if (pillOpen) pillRef.current?.scrollIntoView({ block: 'end' });
  }, [pillOpen]);

  const pillLabel = pollError
    ? 'deploy: error'
    : latestVersion
      ? `update ready${countdown != null ? ` (${countdown}s)` : ''}`
      : bootedVersion
        ? `v ${shortVersion(bootedVersion)}`
        : 'deploy: …';

  return (
    <>
      {latestVersion && (
        <div
          className="seed-banner"
          style={{
            background: '#1e3a8a',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '8px 16px',
          }}
        >
          <span>
            A new version of Nucleus is available.{' '}
            {later
              ? 'Reload when you’re ready.'
              : `Reloading in ${countdown ?? COUNTDOWN_SECONDS}s…`}
            {bootedVersion && (
              <span style={{ marginLeft: 8, opacity: 0.7, fontSize: '0.85em' }}>
                (v: {shortVersion(bootedVersion)} → {shortVersion(latestVersion)})
              </span>
            )}
          </span>
          <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
            Reload now
          </button>
          {!later && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setLater(true); setCountdown(null); }}
            >
              Later
            </button>
          )}
        </div>
      )}

      {/* Always-on status pill, rendered into the sidebar. Click to expand. */}
      {pillHost && createPortal(
      <div className="deploy-pill" ref={pillRef} style={{ userSelect: 'none' }}>
        {pillOpen && (
          <div
            style={{
              background: 'rgba(15, 23, 42, 0.95)',
              color: '#e2e8f0',
              padding: '10px 12px',
              borderRadius: 8,
              marginBottom: 6,
              minWidth: 220,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              lineHeight: 1.5,
            }}
          >
            <div><strong>Deploy watcher</strong></div>
            <div>booted: <code>{shortVersion(bootedVersion) || '—'}</code></div>
            <div>server: <code>{shortVersion(serverVersion) || '—'}</code></div>
            <div>last update: {formatLocalDateTime(bootedAt)}</div>
            <div>last poll: {formatTimeAgo(lastPollAt)}</div>
            {pollError && <div style={{ color: '#fca5a5' }}>error: {pollError}</div>}
            {latestVersion && <div style={{ color: '#fdba74' }}>update detected</div>}
            <div style={{ marginTop: 6, opacity: 0.7 }}>
              Polls every 30s + on focus / online / tab visible.
            </div>
          </div>
        )}
        <button
          onClick={() => setPillOpen(o => !o)}
          style={{
            background: 'rgba(30, 41, 59, 0.85)',
            color: '#f1f5f9',
            border: 'none',
            borderRadius: 999,
            padding: '4px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontFamily: 'system-ui, sans-serif',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
          title="Deploy watcher status (click to expand)"
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: pillDot }} />
          {pillLabel}
        </button>
      </div>
      , pillHost)}
    </>
  );
}
